/**
 * CPT Prompt Manager Component
 *
 * Manages CPT-specific addendum prompts that are appended to the generic
 * report generation prompt. Provides a searchable list of CPT codes with
 * their custom prompts.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import apiClient from '../services/apiClient';
import { typography } from '~/theme';

export interface CPTPromptEntry {
  cpt_code: string | null;
  exam_name: string;
  exam_name_formal: string;
  modality: string | null;
  body_region: string | null;
  custom_prompt: string;
  is_modality_fallback: boolean;
  synonyms: string[];
  template_sections: string[];
  clinical_keywords: string[];
  validation_rules: Record<string, unknown>[];
}

interface Props {
  onSnack: (message: string, severity: 'success' | 'error' | 'info') => void;
}

const CPTPromptManager: React.FC<Props> = ({ onSnack }) => {
  const [prompts, setPrompts] = useState<CPTPromptEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCode, setExpandedCode] = useState<string | null>(null);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Partial<CPTPromptEntry>>({});
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Fetch CPT prompts
  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/v1/cpt-prompts');
        setPrompts(response.data.prompts || []);
      } catch (error) {
        onSnack('Failed to load CPT prompts', 'error');
        console.error('Error fetching CPT prompts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPrompts();
  }, [onSnack, reloadKey]);

  // Filter prompts based on search term
  const filteredPrompts = useMemo(() => {
    if (!searchTerm.trim()) return prompts;
    const term = searchTerm.toLowerCase();
    return prompts.filter(
      (p) =>
        (p.cpt_code?.toLowerCase().includes(term) ?? false) ||
        p.exam_name.toLowerCase().includes(term) ||
        (p.modality?.toLowerCase().includes(term) ?? false) ||
        (p.body_region?.toLowerCase().includes(term) ?? false)
    );
  }, [prompts, searchTerm]);


  const handleExpand = (code: string | null) => {
    setExpandedCode((prev) => (prev === code ? null : code));
    setEditingCode(null);
    setValidationErrors({});
  };

  const handleStartEdit = (prompt: CPTPromptEntry) => {
    if (!prompt.cpt_code) return;
    setEditingCode(prompt.cpt_code);
    setEditDraft({ ...prompt });
    setExpandedCode(prompt.cpt_code);
    setValidationErrors({});
  };

  const handleCancelEdit = () => {
    setEditingCode(null);
    setEditDraft({});
    setValidationErrors({});
  };

  const validateDraft = (draft: Partial<CPTPromptEntry>): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (draft.custom_prompt !== undefined && draft.custom_prompt.trim().length < 10) {
      errors.custom_prompt = 'Custom prompt should be at least 10 characters';
    }
    if (draft.synonyms !== undefined) {
      try { JSON.stringify(draft.synonyms); } catch { errors.synonyms = 'Invalid synonyms format'; }
    }
    if (draft.template_sections !== undefined) {
      try { JSON.stringify(draft.template_sections); } catch { errors.template_sections = 'Invalid template sections format'; }
    }
    if (draft.clinical_keywords !== undefined) {
      try { JSON.stringify(draft.clinical_keywords); } catch { errors.clinical_keywords = 'Invalid clinical keywords format'; }
    }
    return errors;
  };

  const updateDraftField = <K extends keyof CPTPromptEntry>(
    field: K,
    value: CPTPromptEntry[K]
  ) => {
    setEditDraft((prev) => {
      const next = { ...prev, [field]: value };
      setValidationErrors(validateDraft(next));
      return next;
    });
  };

  const handleSave = async () => {
    if (!editingCode) {
      onSnack('Cannot save: no CPT code selected', 'error');
      return;
    }
    const errors = validateDraft(editDraft);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      onSnack('Please fix validation errors before saving', 'error');
      return;
    }

    try {
      setSaving(true);
      await apiClient.put(`/api/v1/cpt-prompts/${editingCode}`, {
        custom_prompt: editDraft.custom_prompt,
        exam_name_formal: editDraft.exam_name_formal,
        synonyms: editDraft.synonyms,
        template_sections: editDraft.template_sections,
        clinical_keywords: editDraft.clinical_keywords,
        validation_rules: editDraft.validation_rules,
      });

      onSnack(`Updated CPT prompt for ${editingCode}`, 'success');
      setEditingCode(null);
      setEditDraft({});
      setValidationErrors({});
      setReloadKey((k) => k + 1);
    } catch (error) {
      onSnack('Failed to save CPT prompt', 'error');
      console.error('Error saving CPT prompt:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleReload = async () => {
    try {
      setLoading(true);
      const response = await apiClient.post('/api/v1/cpt-prompts/reload');
      onSnack(`Reloaded ${response.data.count} CPT prompts`, 'success');
      setReloadKey((k) => k + 1);
    } catch (error) {
      onSnack('Failed to reload CPT prompts', 'error');
      console.error('Error reloading CPT prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getModalityColor = (modality: string | null): 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' => {
    switch (modality?.toLowerCase()) {
      case 'ct':
        return 'primary';
      case 'mri':
        return 'secondary';
      case 'x-ray':
      case 'xr':
        return 'info';
      case 'ultrasound':
      case 'us':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading && prompts.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search by CPT code, exam name, modality, or body region..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: 1, minWidth: 300 }}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Tooltip title="Reload from disk">
          <IconButton onClick={handleReload} disabled={loading} size="small">
            {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 2 }} variant="outlined">
        CPT-specific prompts are appended to the generic report generation prompt. 
        These provide body-part-specific guidance for organizing findings.
      </Alert>

      {/* Prompts Table */}
      <Card variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'background.default' }}>
                <TableCell sx={{ fontWeight: typography.label.fontWeight, width: 40 }} />
                <TableCell sx={{ fontWeight: typography.label.fontWeight }}>CPT Code</TableCell>
                <TableCell sx={{ fontWeight: typography.label.fontWeight }}>Exam Name</TableCell>
                <TableCell sx={{ fontWeight: typography.label.fontWeight }}>Modality</TableCell>
                <TableCell sx={{ fontWeight: typography.label.fontWeight }}>Body Region</TableCell>
                <TableCell sx={{ fontWeight: typography.label.fontWeight }}>Has Custom Prompt</TableCell>
                <TableCell sx={{ fontWeight: typography.label.fontWeight, width: 100 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPrompts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No CPT prompts found matching your search
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPrompts.map((prompt) => {
                  const isExpanded = expandedCode === prompt.cpt_code;
                  const isEditing = editingCode === prompt.cpt_code;
                  const draft = isEditing ? editDraft : prompt;
                  const hasErrors = Object.keys(validationErrors).length > 0 && isEditing;

                  return (
                    <React.Fragment key={prompt.cpt_code || prompt.exam_name}>
                      <TableRow
                        hover
                        sx={{
                          cursor: 'pointer',
                          ...(prompt.is_modality_fallback && {
                            backgroundColor: 'action.hover',
                          }),
                        }}
                        onClick={() => handleExpand(prompt.cpt_code)}
                      >
                        <TableCell sx={{ p: 0, width: 40 }}>
                          <IconButton size="small">
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            fontWeight={prompt.cpt_code ? 600 : 400}
                            color={prompt.cpt_code ? 'text.primary' : 'text.secondary'}
                          >
                            {prompt.cpt_code || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{prompt.exam_name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={prompt.modality || 'N/A'}
                            size="small"
                            color={getModalityColor(prompt.modality)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {prompt.body_region || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {prompt.custom_prompt ? (
                            <Chip label="Yes" size="small" color="success" variant="outlined" />
                          ) : (
                            <Chip label="No" size="small" color="default" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Edit all fields">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartEdit(prompt);
                              }}
                              disabled={!prompt.cpt_code}
                              color={isEditing ? 'primary' : 'default'}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>

                      {/* Expanded detail row */}
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ p: 0, borderBottom: 0 }}>
                            <Box sx={{ px: 3, py: 2, backgroundColor: 'background.default' }}>
                              {/* Schema fields */}
                              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
                                <TextField
                                  label="Formal Exam Name"
                                  size="small"
                                  value={draft?.exam_name_formal || ''}
                                  onChange={(e) => updateDraftField('exam_name_formal', e.target.value)}
                                  disabled={!isEditing}
                                  fullWidth
                                />
                                <TextField
                                  label="Synonyms (comma-separated)"
                                  size="small"
                                  value={(draft?.synonyms || []).join(', ')}
                                  onChange={(e) => updateDraftField('synonyms', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                                  disabled={!isEditing}
                                  fullWidth
                                  error={!!validationErrors.synonyms}
                                  helperText={validationErrors.synonyms}
                                />
                                <TextField
                                  label="Template Sections (comma-separated)"
                                  size="small"
                                  value={(draft?.template_sections || []).join(', ')}
                                  onChange={(e) => updateDraftField('template_sections', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                                  disabled={!isEditing}
                                  fullWidth
                                  error={!!validationErrors.template_sections}
                                  helperText={validationErrors.template_sections}
                                />
                                <TextField
                                  label="Clinical Keywords (comma-separated)"
                                  size="small"
                                  value={(draft?.clinical_keywords || []).join(', ')}
                                  onChange={(e) => updateDraftField('clinical_keywords', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                                  disabled={!isEditing}
                                  fullWidth
                                  error={!!validationErrors.clinical_keywords}
                                  helperText={validationErrors.clinical_keywords}
                                />
                              </Box>

                              {/* Custom prompt */}
                              <Typography variant="subtitle2" gutterBottom>
                                Custom Prompt Addendum
                              </Typography>
                              <TextField
                                fullWidth
                                multiline
                                rows={6}
                                value={draft?.custom_prompt || ''}
                                onChange={(e) => updateDraftField('custom_prompt', e.target.value)}
                                disabled={!isEditing}
                                placeholder="Enter CPT-specific guidance for organizing findings..."
                                error={!!validationErrors.custom_prompt}
                                helperText={validationErrors.custom_prompt}
                                InputProps={{
                                  sx: { fontFamily: 'monospace', fontSize: typography.body2.fontSize },
                                }}
                              />

                              {/* Live Preview */}
                              <Alert severity="info" sx={{ mt: 2 }} variant="outlined">
                                <Typography variant="body2">
                                  <strong>Preview:</strong> The generic prompt will be combined with this addendum:
                                </Typography>
                                <Box
                                  component="pre"
                                  sx={{
                                    mt: 1,
                                    p: 1,
                                    backgroundColor: 'background.paper',
                                    borderRadius: 1,
                                    fontSize: typography.caption.fontSize,
                                    overflow: 'auto',
                                    maxHeight: 150,
                                  }}
                                >
                                  {`[Generic Report Prompt]\n\n---\n\nCPT-SPECIFIC GUIDANCE (${draft?.cpt_code || 'N/A'}):\n${draft?.custom_prompt || '[No addendum configured]'}`}
                                </Box>
                              </Alert>

                              {/* Validation status */}
                              {isEditing && (
                                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {hasErrors ? (
                                    <>
                                      <ErrorIcon color="error" fontSize="small" />
                                      <Typography variant="body2" color="error">
                                        {Object.keys(validationErrors).length} validation error(s)
                                      </Typography>
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircleIcon color="success" fontSize="small" />
                                      <Typography variant="body2" color="success">
                                        All fields valid
                                      </Typography>
                                    </>
                                  )}
                                </Box>
                              )}

                              {/* Edit actions */}
                              {isEditing && (
                                <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                  <Button
                                    size="small"
                                    onClick={handleCancelEdit}
                                    startIcon={<CloseIcon />}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={handleSave}
                                    startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                                    disabled={saving || hasErrors}
                                  >
                                    Save
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

    </Box>
  );
};

export default CPTPromptManager;
